'use client';

import React, { useState, useRef, useEffect } from 'react';

const DISCIPLINES = [
  { value: '', label: 'Select your discipline' },
  { value: 'editing', label: 'Editing' },
  { value: 'motion-graphics', label: 'Motion Graphics' },
  { value: 'colour', label: 'Colour Grading' },
  { value: 'sound', label: 'Sound Design' },
  { value: 'directing', label: 'Directing' },
];

export default function SignupSection() {
  const [form, setForm] = useState({ name: '', email: '', discipline: '' });
  const [submitted, setSubmitted] = useState(false);
  const [syllabusSubmitted, setSyllabusSubmitted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const els = sectionRef.current?.querySelectorAll('.reveal-hidden');
    els?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submit handler — connect to backend/email service here
    setSubmitted(true);
  };

  const handleSyllabus = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock PDF gate — connect to email capture service here
    setSyllabusSubmitted(true);
  };

  return (
    <section
      id="signup"
      ref={sectionRef}
      className="py-24 px-6 relative overflow-hidden grain-overlay"
      style={{
        background: 'linear-gradient(180deg, #0B1A2E 0%, #060F1C 100%)',
        borderTop: '1px solid rgba(244,246,248,0.06)',
      }}
    >
      {/* Green glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px',
          height: '400px',
          background: 'radial-gradient(ellipse, rgba(0,230,118,0.06) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <div className="reveal-hidden text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 text-xs font-semibold tracking-widest uppercase"
            style={{ background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.15)', color: '#00E676' }}
          >
            <span className="blink w-1.5 h-1.5 rounded-full" style={{ background: '#00E676' }} />
            Free Module — No Card Required
          </div>
          <h2
            className="font-display font-light leading-none tracking-tight mb-4"
            style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', color: '#F4F6F8' }}
          >
            Sit down.{' '}
            <em style={{ color: 'rgba(244,246,248,0.45)' }}>Everything's</em>
            <br />
            already open.
          </h2>
          <p
            className="text-base font-light max-w-lg mx-auto"
            style={{ color: 'rgba(244,246,248,0.5)' }}
          >
            Your free module runs on a real Avid or DaVinci Resolve workstation — remote-access, 
            fully configured. First name, email, discipline. That's it.
          </p>
        </div>

        {/* Main form + secondary path */}
        <div className="grid md:grid-cols-5 gap-8 items-start">

          {/* Primary: Free Module Signup */}
          <div
            className="reveal-hidden md:col-span-3 rounded-2xl p-8"
            style={{
              background: 'linear-gradient(135deg, rgba(58,74,92,0.2) 0%, rgba(15,34,64,0.3) 100%)',
              border: '1px solid rgba(244,246,248,0.1)',
            }}
          >
            {submitted ? (
              <div className="text-center py-8">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'rgba(0,230,118,0.12)', border: '1px solid rgba(0,230,118,0.3)' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00E676" strokeWidth="2.5">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h3 className="font-display font-light text-2xl mb-2" style={{ color: '#F4F6F8' }}>
                  You're in the suite.
                </h3>
                <p className="text-sm" style={{ color: 'rgba(244,246,248,0.55)' }}>
                  Check your inbox — your workstation access link arrives within 5 minutes.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <span className="blink w-2 h-2 rounded-full" style={{ background: '#00E676' }} />
                  <span className="text-sm font-semibold" style={{ color: '#00E676' }}>
                    Start Your Free Module
                  </span>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs font-semibold tracking-widest uppercase mb-2"
                      style={{ color: 'rgba(244,246,248,0.45)' }}
                    >
                      First Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      required
                      placeholder="e.g. Jess"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="form-input w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200"
                      style={{
                        background: 'rgba(244,246,248,0.06)',
                        border: '1px solid rgba(244,246,248,0.12)',
                        color: '#F4F6F8',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-semibold tracking-widest uppercase mb-2"
                      style={{ color: 'rgba(244,246,248,0.45)' }}
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      placeholder="jess@yourstudio.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="form-input w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200"
                      style={{
                        background: 'rgba(244,246,248,0.06)',
                        border: '1px solid rgba(244,246,248,0.12)',
                        color: '#F4F6F8',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="discipline"
                      className="block text-xs font-semibold tracking-widest uppercase mb-2"
                      style={{ color: 'rgba(244,246,248,0.45)' }}
                    >
                      Creative Discipline
                    </label>
                    <select
                      id="discipline"
                      required
                      value={form.discipline}
                      onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                      className="form-input w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer"
                      style={{
                        background: 'rgba(244,246,248,0.06)',
                        border: '1px solid rgba(244,246,248,0.12)',
                        color: form.discipline ? '#F4F6F8' : 'rgba(244,246,248,0.35)',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                      }}
                    >
                      {DISCIPLINES.map((d) => (
                        <option
                          key={d.value}
                          value={d.value}
                          style={{ background: '#0B1A2E', color: '#F4F6F8' }}
                        >
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="btn-magnetic pulse-green w-full py-3.5 rounded-xl text-sm font-semibold mt-2 flex items-center justify-center gap-2"
                    style={{ background: '#00E676', color: '#0B1A2E' }}
                  >
                    <span className="blink w-2 h-2 rounded-full" style={{ background: '#0B1A2E', opacity: 0.7 }} />
                    Access My Free Module
                  </button>

                  <p className="text-xs text-center" style={{ color: 'rgba(244,246,248,0.3)' }}>
                    No credit card. No commitment. Cancel anytime.
                  </p>
                </form>
              </>
            )}
          </div>

          {/* Secondary: Syllabus Download */}
          <div
            className="reveal-hidden md:col-span-2 rounded-2xl p-7"
            style={{
              background: 'rgba(244,246,248,0.03)',
              border: '1px solid rgba(244,246,248,0.08)',
              transitionDelay: '150ms',
            }}
          >
            <div className="mb-5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ background: 'rgba(244,246,248,0.06)', border: '1px solid rgba(244,246,248,0.1)' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(244,246,248,0.6)" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              </div>
              <h3 className="font-display font-light text-xl mb-2" style={{ color: '#F4F6F8' }}>
                Download the Full Syllabus
              </h3>
              <p className="text-sm" style={{ color: 'rgba(244,246,248,0.45)' }}>
                Not ready to start? The full 48-page curriculum PDF covers every session, every tool, 
                and every broadcast standard you'll work to.
              </p>
            </div>

            {syllabusSubmitted ? (
              <div
                className="p-4 rounded-xl text-center"
                style={{ background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.2)' }}
              >
                <p className="text-sm font-semibold" style={{ color: '#00E676' }}>
                  Syllabus on its way.
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(244,246,248,0.45)' }}>
                  Check your inbox in the next 2 minutes.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSyllabus} className="space-y-3">
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  className="form-input w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200"
                  style={{
                    background: 'rgba(244,246,248,0.06)',
                    border: '1px solid rgba(244,246,248,0.1)',
                    color: '#F4F6F8',
                  }}
                />
                <button
                  type="submit"
                  className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200"
                  style={{
                    border: '1px solid rgba(244,246,248,0.2)',
                    color: 'rgba(244,246,248,0.8)',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(244,246,248,0.06)';
                    (e.currentTarget as HTMLElement).style.color = '#F4F6F8';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = 'rgba(244,246,248,0.8)';
                  }}
                >
                  Send Me the Syllabus PDF
                </button>
              </form>
            )}

            {/* What's inside */}
            <div className="mt-6 space-y-2.5">
              {[
                '48-page curriculum breakdown',
                'Session-by-session tool list',
                'Broadcast standard benchmarks',
                'Alumni outcome data',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(0,230,118,0.1)' }}
                  >
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#00E676" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <span className="text-xs" style={{ color: 'rgba(244,246,248,0.5)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}